import type { Task, Priority, Habit, HabitCompletion, CalendarEvent } from '@/types';
import { toDateKey, isSameLocalDay } from '@/lib/dateKey';
import { formatTimeFromDate, formatRelative, type RelativeTime } from '@/lib/time';

export type { RelativeTime };
import { isHabitDueOnDay } from '@/lib/week';
import type { WeekStart } from '@/lib/week';

export interface ScheduledHabit {
  habit: Habit;
  start: Date;
  end: Date;
  isDone: boolean;
}

function scheduleHabitForDate(habit: Habit, viewedDate: Date): { start: Date; end: Date } | null {
  if (!habit.scheduledTime) return null;
  const parts = habit.scheduledTime.split(':').map(Number);
  const hh = parts[0] ?? 0;
  const mm = parts[1] ?? 0;
  const start = new Date(viewedDate);
  start.setHours(hh, mm, 0, 0);
  const end = new Date(start.getTime() + (habit.durationMinutes ?? 15) * 60_000);
  return { start, end };
}

/**
 * The habit to highlight in the NOW card when no task is in_progress / now-active.
 * Returns the scheduled habit whose [start, end) window contains `now` on today.
 */
export function getCurrentHabit(
  habits: Habit[],
  completions: HabitCompletion[],
  now: Date,
  viewedDate: Date,
  weekStart: WeekStart = 1,
): ScheduledHabit | undefined {
  if (!isSameDay(now, viewedDate)) return undefined;
  const dateKey = toDateKey(viewedDate);
  for (const h of habits) {
    if (!isHabitDueOnDay(h, viewedDate.getDay(), weekStart)) continue;
    const sched = scheduleHabitForDate(h, viewedDate);
    if (!sched) continue;
    if (sched.start.getTime() <= now.getTime() && sched.end.getTime() > now.getTime()) {
      const isDone = completions.some((c) => c.habitId === h.id && c.date === dateKey);
      return { habit: h, start: sched.start, end: sched.end, isDone };
    }
  }
  return undefined;
}

/**
 * Scheduled habits to interleave into Up Next.
 * - Only habits applicable to `viewedDate`'s day-of-week.
 * - When viewing today: only habits starting after `now`.
 * - When viewing another day: all scheduled habits for that day.
 * Sorted ascending by start.
 */
export function getUpNextHabits(
  habits: Habit[],
  completions: HabitCompletion[],
  now: Date,
  viewedDate: Date,
  weekStart: WeekStart = 1,
): ScheduledHabit[] {
  const viewingToday = isSameDay(now, viewedDate);
  const dateKey = toDateKey(viewedDate);
  const items: ScheduledHabit[] = [];
  for (const h of habits) {
    if (!isHabitDueOnDay(h, viewedDate.getDay(), weekStart)) continue;
    const sched = scheduleHabitForDate(h, viewedDate);
    if (!sched) continue;
    if (viewingToday && sched.start.getTime() <= now.getTime()) continue;
    const isDone = completions.some((c) => c.habitId === h.id && c.date === dateKey);
    items.push({ habit: h, start: sched.start, end: sched.end, isDone });
  }
  return items.sort((a, b) => a.start.getTime() - b.start.getTime());
}

// Canonical implementation lives in lib/dateKey — re-exported under the name
// this module's consumers already import.
export const isSameDay = isSameLocalDay;

/**
 * The task to highlight in the NOW card. Priority order:
 * 1. Any task currently in_progress.
 * 2. The scheduled task whose [start, end) window contains `now` — only when the
 *    viewed date is today (you don't want a "Now" card while browsing tomorrow).
 */
export function getCurrentTask(tasks: Task[], now: Date, viewedDate: Date): Task | undefined {
  const inProgress = tasks.find((t) => t.status === 'in_progress');
  if (inProgress) return inProgress;

  if (!isSameDay(now, viewedDate)) return undefined;

  return tasks.find(
    (t) =>
      t.status === 'scheduled' &&
      t.scheduledStart !== undefined &&
      t.scheduledEnd !== undefined &&
      t.scheduledStart.getTime() <= now.getTime() &&
      t.scheduledEnd.getTime() > now.getTime(),
  );
}

/**
 * A single item concurrent with `now` — the shape the Now stack's expanded
 * hero and compact rows share. Mirrors UpNextItem, plus `end` (a now-item is
 * mid-window, so both edges matter for display and ordering).
 */
export type NowItem =
  | { kind: 'task'; task: Task; start: Date; end: Date }
  | { kind: 'habit'; habit: Habit; start: Date; end: Date; isDone: boolean }
  | { kind: 'calendar-event'; event: CalendarEvent; start: Date; end: Date };

const KIND_RANK: Record<NowItem['kind'], number> = { task: 0, habit: 1, 'calendar-event': 2 };

function isInProgress(i: NowItem): boolean {
  return i.kind === 'task' && i.task.status === 'in_progress';
}

/**
 * Display order for the Now stack: start ascending, ties broken by
 * in_progress first, then task > habit > meeting, then end ascending. Stable
 * regardless of which item is expanded — rows never reorder on interaction.
 */
function byDisplayOrder(a: NowItem, b: NowItem): number {
  return (
    a.start.getTime() - b.start.getTime() ||
    Number(isInProgress(b)) - Number(isInProgress(a)) ||
    KIND_RANK[a.kind] - KIND_RANK[b.kind] ||
    a.end.getTime() - b.end.getTime()
  );
}

/** Stable identity for a now-item — the NowStack expansion key ("kind:id"). */
export function nowItemKey(item: NowItem): string {
  switch (item.kind) {
    case 'task':
      return `task:${item.task.id}`;
    case 'habit':
      return `habit:${item.habit.id}`;
    case 'calendar-event':
      return `calendar-event:${item.event.id}`;
  }
}

/**
 * The item the Now stack expands when the user hasn't chosen one: an
 * in_progress task if any, else the earliest-starting item by type priority
 * task > habit > meeting — the old single-hero cascade's heuristic, so the
 * default hero never regresses. Expects `items` in getNowItems' start-sorted
 * display order (the first match per kind is then the earliest-starting one).
 */
export function getDefaultNowKey(items: NowItem[]): string | undefined {
  const live = items.find(isInProgress);
  if (live) return nowItemKey(live);
  for (const kind of ['task', 'habit', 'calendar-event'] as const) {
    const first = items.find((i) => i.kind === kind);
    if (first) return nowItemKey(first);
  }
  return undefined;
}

function toTaskNowItem(t: Task, now: Date): NowItem {
  // An in_progress task may have no schedule (started straight from Inbox) —
  // synthesize a window from now + duration so the row still shows a range.
  const start = t.scheduledStart ?? now;
  const end = t.scheduledEnd ?? new Date(start.getTime() + t.durationMinutes * 60_000);
  return { kind: 'task', task: t, start, end };
}

/**
 * ALL items whose window contains `now`, in stable display order (see
 * byDisplayOrder: start asc, in_progress first on ties, then task > habit >
 * meeting, then end asc). Replaces the mutual suppression between
 * getCurrentTask / getCurrentHabit / getCurrentCalendarEvent so concurrent
 * items are no longer hidden. Which item expands by default is a separate
 * question — see getDefaultNowKey.
 *
 * Rules:
 * - Non-today viewed date → empty, EXCEPT an in_progress task still surfaces
 *   (getCurrentTask's semantics: in_progress wins before the same-day check).
 * - Dedupe: a now-task that references a now-calendar-event via
 *   `calendarEventId` is that event's actionable twin — the event is dropped.
 */
export function getNowItems(
  tasks: Task[],
  habits: Habit[],
  completions: HabitCompletion[],
  events: CalendarEvent[],
  now: Date,
  viewedDate: Date,
  weekStart: WeekStart = 1,
): NowItem[] {
  const nowMs = now.getTime();
  const firstInProgress = tasks.find((t) => t.status === 'in_progress');

  if (!isSameDay(now, viewedDate)) {
    return firstInProgress ? [toTaskNowItem(firstInProgress, now)] : [];
  }

  const nowTasks: NowItem[] = tasks
    .filter(
      (t) =>
        t.status === 'in_progress' ||
        (t.status === 'scheduled' &&
          t.scheduledStart !== undefined &&
          t.scheduledEnd !== undefined &&
          t.scheduledStart.getTime() <= nowMs &&
          t.scheduledEnd.getTime() > nowMs),
    )
    .map((t) => toTaskNowItem(t, now));

  const dateKey = toDateKey(viewedDate);
  const nowHabits: NowItem[] = [];
  for (const h of habits) {
    if (!isHabitDueOnDay(h, viewedDate.getDay(), weekStart)) continue;
    const sched = scheduleHabitForDate(h, viewedDate);
    if (!sched) continue;
    if (sched.start.getTime() <= nowMs && sched.end.getTime() > nowMs) {
      const isDone = completions.some((c) => c.habitId === h.id && c.date === dateKey);
      nowHabits.push({ kind: 'habit', habit: h, start: sched.start, end: sched.end, isDone });
    }
  }

  const claimedEventIds = new Set<string>();
  for (const item of nowTasks) {
    if (item.kind === 'task' && item.task.calendarEventId !== undefined) {
      claimedEventIds.add(item.task.calendarEventId);
    }
  }
  const nowEvents: NowItem[] = events
    .filter(
      (e) =>
        !e.isAllDay &&
        !claimedEventIds.has(e.id) &&
        e.start.getTime() <= nowMs &&
        e.end.getTime() > nowMs &&
        isSameDay(e.start, viewedDate),
    )
    .map((e) => ({ kind: 'calendar-event' as const, event: e, start: e.start, end: e.end }));

  return [...nowTasks, ...nowHabits, ...nowEvents].sort(byDisplayOrder);
}

export interface GetUpNextOptions {
  limit?: number;
  excludeId?: string;
  /** Bulk exclusion — used to keep every "now" task out of Up next. */
  excludeIds?: ReadonlySet<string>;
}

/**
 * Tasks the user should see after (or in place of) the NOW card.
 * - Same day as viewedDate.
 * - status: scheduled or in_progress.
 * - When viewing today: only items starting after now.
 * - When viewing another day: all scheduled items for that day.
 * Sorted ascending by scheduledStart.
 */
export function getUpNextTasks(
  tasks: Task[],
  now: Date,
  viewedDate: Date,
  options: GetUpNextOptions = {},
): Task[] {
  const { limit = 5, excludeId, excludeIds } = options;
  const viewingToday = isSameDay(now, viewedDate);

  return tasks
    .filter((t) => t.id !== excludeId && !excludeIds?.has(t.id))
    .filter((t) => t.status === 'scheduled' || t.status === 'in_progress')
    .filter((t) => t.scheduledStart !== undefined && isSameDay(t.scheduledStart, viewedDate))
    .filter((t) => (viewingToday ? t.scheduledStart!.getTime() > now.getTime() : true))
    .sort((a, b) => a.scheduledStart!.getTime() - b.scheduledStart!.getTime())
    .slice(0, limit);
}

export interface TodayCounts {
  scheduled: number;
  done: number;
  total: number;
}

export function getTodayCounts(tasks: Task[], viewedDate: Date): TodayCounts {
  let scheduled = 0;
  let done = 0;
  for (const t of tasks) {
    if (!t.scheduledStart || !isSameDay(t.scheduledStart, viewedDate)) continue;
    if (t.status === 'done') done++;
    else if (t.status === 'scheduled' || t.status === 'in_progress') scheduled++;
  }
  return { scheduled, done, total: scheduled + done };
}

export interface DayLoad {
  scheduledTasks: number;
  doneTasks: number;
  events: number;
  habits: number;
  /** True only when the day has no tasks, meetings, or habits at all. */
  empty: boolean;
}

/**
 * Whole-day load for the Today header summary. Counts tasks AND calendar
 * events AND habits, so a day full of meetings/habits is no longer reported
 * as an "Open day" (which only looked at tasks).
 */
export function getDayLoad(
  tasks: Task[],
  events: CalendarEvent[],
  habits: Habit[],
  viewedDate: Date,
  weekStart: WeekStart = 1,
): DayLoad {
  const counts = getTodayCounts(tasks, viewedDate);
  const eventCount = events.filter((e) => isSameDay(e.start, viewedDate)).length;
  const dow = viewedDate.getDay();
  const habitCount = habits.filter((h) => isHabitDueOnDay(h, dow, weekStart)).length;
  return {
    scheduledTasks: counts.scheduled,
    doneTasks: counts.done,
    events: eventCount,
    habits: habitCount,
    empty: counts.total === 0 && eventCount === 0 && habitCount === 0,
  };
}

/**
 * Whether to render the idle "now" card (which reads "Nothing happening right
 * now" on today, or "Open day" on another day). Show it when viewing today (the
 * between-tasks gap state) or when the viewed day has nothing to show. Suppress
 * it on a NON-today day that has Up-next items — otherwise a future day full of
 * meetings/habits wrongly reads "Open day" while its agenda is listed right below.
 */
export function shouldShowNoCurrentCard(isViewingToday: boolean, upNextCount: number): boolean {
  return isViewingToday || upNextCount === 0;
}

/** "now", "in 8m", "1h 42m ago" — delegates to the canonical compact style. */
export const relativeMinutesLabel = (target: Date, now: Date): RelativeTime =>
  formatRelative(target, now, 'compact');

/**
 * Column/row time on the Today surfaces — delegates to the canonical human
 * format ("9 AM", "9:30 AM"). The earlier local version dropped AM/PM
 * ("3:00"), which was ambiguous and off the time-is-human voice rule.
 */
export function formatTimeShort(d: Date): string {
  return formatTimeFromDate(d);
}

export function progressPct(start: Date, end: Date, now: Date): number {
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  const elapsed = now.getTime() - start.getTime();
  return Math.max(0, Math.min(1, elapsed / total));
}

/**
 * Tailwind class for the priority accent strip on Up Next rows.
 * The full class strings appear here so Tailwind's content scanner picks them up.
 */
export function priorityAccentClass(p: Priority): string {
  switch (p) {
    case 'critical':
      return 'bg-crit';
    case 'high':
      return 'bg-high';
    case 'medium':
      return 'bg-med';
    case 'low':
      return 'bg-low';
  }
}

export function priorityLabel(p: Priority): string {
  switch (p) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
  }
}

/**
 * The meeting to surface in the "Right now" hero when no task or habit claims
 * the slot. Only on today; all-day events never take over the Now card.
 */
export function getCurrentCalendarEvent(
  events: CalendarEvent[],
  now: Date,
  viewedDate: Date,
): CalendarEvent | undefined {
  if (!isSameDay(now, viewedDate)) return undefined;
  return events.find(
    (e) =>
      !e.isAllDay &&
      e.start.getTime() <= now.getTime() &&
      e.end.getTime() > now.getTime() &&
      isSameDay(e.start, viewedDate),
  );
}

/**
 * Calendar events to interleave into Up Next.
 * - Only non-all-day events on `viewedDate`.
 * - When viewing today: only events that have not yet ended (end > now).
 * - When viewing another day: all events for that day.
 * Sorted ascending by start.
 */
export function getUpNextCalendarEvents(
  events: CalendarEvent[],
  now: Date,
  viewedDate: Date,
): CalendarEvent[] {
  const viewingToday = isSameDay(now, viewedDate);
  return events
    .filter((e) => !e.isAllDay)
    .filter((e) => isSameDay(e.start, viewedDate))
    .filter((e) => (viewingToday ? e.end.getTime() > now.getTime() : true))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * ISO 8601 week number — Mon-based, Thursday-anchored. Matches what most
 * European date pickers and `date-fns/getISOWeek` produce.
 */
export function isoWeekNumber(d: Date): number {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = (target.getTime() - firstThursday.getTime()) / 86_400_000;
  return 1 + Math.round((diff - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}
